

import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import {
  Box, Typography, Button, IconButton, Paper, TextField, Switch, FormControlLabel,
  Grid, MenuItem, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Stack,
  List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Add, ArrowBack, Delete, Edit, Sort, DragIndicator, InfoOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getCvFormDetails,
  getCvFormQuestions,
  createCvQuestion,
  updateCvQuestion,
  deleteCvQuestion,
  saveCvQuestionOptions,
  saveCvQuestionRules,
  updateCvForm,
  reorderCvQuestions,
} from '../../../../services/api';
import ConfirmDialog from '../../../common/ConfirmDialog';

/**
 * Constante com os tipos de perguntas disponíveis no editor.
 */
const QUESTION_TYPES = [
  { value: 'text', label: 'Texto' },
  { value: 'number', label: 'Número' },
  { value: 'select', label: 'Múltipla Escolha (1 opção)' },
  { value: 'multiselect', label: 'Múltipla Escolha (várias)' },
  { value: 'boolean', label: 'Sim/Não' },
  { value: 'likert', label: 'Escala (1-5)' },
  { value: 'experience', label: 'Experiência Profissional' },
];

/**
 * Operadores de regra para perguntas do tipo 'boolean'.
 */
const BOOLEAN_OPERATORS = [
  { value: '=', label: 'Igual a' },
  { value: '!=', label: 'Diferente de' },
];

/**
 * Operadores de regra para perguntas dos tipos 'number' e 'likert'.
 */
const NUMERIC_OPERATORS = [
  { value: '=', label: 'Igual a' },
  { value: '!=', label: 'Diferente de' },
  { value: '<', label: 'Menor que' },
  { value: '>', label: 'Maior que' },
];

/**
 * Operadores de regra para perguntas dos tipos 'select' e 'multiselect'.
 */
const CHOICE_OPERATORS = [
  { value: '=', label: 'Igual a' },
  { value: '!=', label: 'Diferente de' },
];

/**
 * Operadores de regra padrão (usados para 'text').
 */
const TEXT_OPERATORS = [
  { value: '=', label: 'Igual a' },
  { value: '!=', label: 'Diferente de' },
];

/**
 * Componente (Dialog) para criar ou editar uma pergunta.
 * Gerencia o estado interno da pergunta, suas opções e regras.
 * @param {{open: boolean, onClose: function, onSave: function, question: object|null}} props
 */
export function QuestionDialog({ open, onClose, onSave, question: initialQuestion }) {
  const [form, setForm] = useState({});
  const [options, setOptions] = useState([]);
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);

  // Inicializa o estado do formulário ao abrir o dialog
  useEffect(() => {
    if (initialQuestion) {
      const { ...rest } = initialQuestion;
      delete rest.weight; // Remove 'weight' se existir
      setForm(rest);
      setOptions(initialQuestion.options || []);
      // Garante que 'experience' tenha no máximo 1 regra ao carregar
      if(rest.type === 'experience' && initialQuestion.rules?.length > 1) {
        setRules([initialQuestion.rules[0]]); // Pega apenas a primeira
      } else {
        setRules(initialQuestion.rules || []);
      }
    } else {
      // Estado padrão para nova pergunta
      setForm({ type: 'text', required: true, label: '' });
      setOptions([]);
      setRules([]);
    }
  }, [initialQuestion, open]);

  /**
   * Manipulador para salvar a pergunta (criar ou atualizar).
   * Salva a pergunta principal, depois salva opções e regras em chamadas separadas.
   */
  const handleSave = async () => {
    // Validações
    if (!form.label?.trim()) return toast.warning('O texto da pergunta é obrigatório.');
    if ((form.type === 'select' || form.type === 'multiselect') && options.length === 0) {
        return toast.warning('Perguntas de múltipla escolha devem ter pelo menos uma opção.');
    }
    if (options.some(opt => !opt.label?.trim())) {
        return toast.warning('Todas as opções devem ter um texto preenchido.');
    }
    if (form.type === 'experience' && rules.length > 0) {
      const ruleValue = rules[0].value_left;
      if (ruleValue === '' || ruleValue === null || Number.isNaN(Number(ruleValue)) || Number(ruleValue) <= 0) {
        return toast.warning('Para a regra de experiência, informe um número de meses válido (maior que zero).');
      }
    }

    setSaving(true);
    try {
        const payload = { ...form };
        delete payload.weight; // Garante que 'weight' não seja enviado

        // 1. Salva a pergunta principal (create ou update)
        const savedQuestion = await onSave(payload);

        if (!savedQuestion?.id) throw new Error("Falha ao salvar a pergunta ou obter seu ID.");

        // 2. Salva opções e regras em paralelo
        await Promise.all([
            saveCvQuestionOptions(savedQuestion.id, options),
            saveCvQuestionRules(savedQuestion.id, rules)
        ]);

        toast.success('Pergunta salva com sucesso!');
        onClose(true); // Fecha o dialog e sinaliza para recarregar
    } catch (err) {
        console.error("Erro detalhado ao salvar:", err);
        const apiMessage = err?.response?.data?.message || err?.response?.data?.error || err?.message;
        toast.error(`Erro ao salvar a pergunta completa: ${apiMessage || 'Verifique o console.'}`);
    } finally {
        setSaving(false);
    }
  };

  // --- Lógica de Opções ---
  const addOption = () => setOptions([...options, { label: '', value: '', position: options.length + 1 }]);
  const updateOption = (index, field, value) => {
    setOptions((prev) => {
      const newOptions = [...prev];
      const option = { ...newOptions[index] };
      if (field === 'label') {
          const previousLabel = option.label;
          option.label = value;
          // Sincroniza 'value' com 'label' se 'value' estiver vazio ou igual ao label anterior
          if (!option.value || option.value === previousLabel) {
              option.value = value;
          }
      } else {
          option[field] = value;
      }
      newOptions[index] = option;
      return newOptions;
    });
  };
  const removeOption = (index) => setOptions(options.filter((_, i) => i !== index));

  // --- Lógica de Regras ---
  /**
   * Adiciona uma nova regra padrão, ajustada para o tipo de pergunta.
   * Impede a adição se for 'experience' e já houver 1 regra.
   */
  const handleAddRule = useCallback(() => {
   if (form.type === 'experience' && rules.length >= 1) {
       return;
   }

   // ✅ CORREÇÃO: Trocado 'compare' por 'autofail' para bater com o CHECK constraint
   let defaultRule = { operator: '=', value: '', value_left: '', decision_on_true: 'fail', rule_type: 'autofail' };
   const type = String(form.type).toLowerCase();

   if(type === 'boolean' || type === 'sim/não') {
      defaultRule.value = 'não';
      defaultRule.value_left = 'não';
   } else if (type === 'number' || type === 'likert' || type === 'escala (1-5)') {
      defaultRule.operator = '<';
      defaultRule.value = '3';
      defaultRule.value_left = '3';
   } else if (type === 'experience') {
       defaultRule.operator = '<';
       defaultRule.value = '6'; // Default 6 meses
       defaultRule.value_left = '6';
   }

   setRules(prev => [...prev, defaultRule]);
  }, [form.type, rules.length]);

  /**
   * Atualiza uma regra específica.
   * Garante que 'value_left' (valor principal) seja sincronizado com 'value' (valor do input).
   * Força o operador '<' para 'experience'.
   */
  const handleUpdateRule = (index, updatedRuleData) => {
    setRules(prev => {
        const newRules = [...prev];
        if (index < 0 || index >= newRules.length) return prev;

        const newValue = updatedRuleData.value;
        newRules[index] = {
            ...newRules[index],
            ...updatedRuleData,
            value_left: newValue, // Sincroniza value_left
            ...(form.type === 'experience' && { operator: '<' }) // Força operador para experience
        };
        return newRules;
    });
  };

  const handleRemoveRule = (index) => {
   setRules(prev => prev.filter((_, i) => i !== index));
  };

  // --- Lógica de Renderização Condicional ---
  const showOptions = form.type === 'select' || form.type === 'multiselect';
  const canHaveRules = form.type !== 'text';
  const isExperience = form.type === 'experience';

  // Efeito para limpar regras se o tipo mudar para 'text'
  // ou adicionar regra padrão para 'experience'
  useEffect(() => {
    if (!canHaveRules && rules.length > 0) {
      setRules([]);
    }
    if (form.type === 'experience' && rules.length > 1) {
        setRules([rules[0]]); // Mantém apenas a primeira
    }
    // Adiciona uma regra padrão automaticamente se for 'experience' e não tiver nenhuma
    if (form.type === 'experience' && rules.length === 0) {
        handleAddRule();
    }
  }, [canHaveRules, rules, form.type, handleAddRule]); // handleAddRule é dependência


  /**
   * Renderiza os inputs corretos (operador, valor) para um tipo de regra.
   * @param {string} questionType - O tipo da pergunta (ex: 'number', 'boolean').
   * @param {object} rule - O objeto da regra.
   * @param {function} onUpdateRule - Callback para atualizar a regra.
   * @param {number} index - O índice da regra.
   * @param {Array<object>} [availableOptions] - Opções (para 'select'/'multiselect').
   * @returns {JSX.Element}
   */
  const renderRuleInputs = (questionType, rule, onUpdateRule, index, availableOptions = []) => {
      const handleOperatorChange = (e) => {
          onUpdateRule(index, { operator: e.target.value });
      };
      const handleValueChange = (e) => {
          onUpdateRule(index, { value: e.target.value });
      };
      const normalizedType = String(questionType).toLowerCase();

      // --- Caso: Sim/Não (boolean) ---
      if (normalizedType === 'boolean' || normalizedType === 'sim/não') {
            return (
              <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={5}>
                      <TextField select fullWidth size="small" label="Condição" value={rule.operator || '='} onChange={handleOperatorChange}>
                          {BOOLEAN_OPERATORS.map(op => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                      </TextField>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                      <TextField select fullWidth size="small" label="Valor" value={rule.value_left || 'não'} onChange={handleValueChange} InputLabelProps={{ shrink: true }}>
                          <MenuItem value="sim">Sim</MenuItem>
                          <MenuItem value="não">Não</MenuItem>
                      </TextField>
                  </Grid>
              </Grid>
          );
      }
      // --- Caso: Número ou Escala ---
      if (normalizedType === 'number' || normalizedType === 'likert' || normalizedType === 'escala (1-5)') {
            return (
              <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={5}>
                      <TextField select fullWidth size="small" label="Condição" value={rule.operator || '<'} onChange={handleOperatorChange}>
                         {NUMERIC_OPERATORS.map(op => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                      </TextField>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                      <TextField fullWidth size="small" label="Valor" type="number" value={rule.value_left || ''} onChange={handleValueChange} InputLabelProps={{ shrink: true }} />
                  </Grid>
              </Grid>
          );
      }
      // --- Caso: Experiência ---
       if (normalizedType === 'experience') {
         return (
              <Grid container spacing={1} alignItems="center">
                   <Grid item xs={12} sm={8}>
                       <Typography variant="body2" sx={{ textAlign: { xs: 'left', sm: 'right' }, mr: 1 }}>
                           Reprovar se a média de permanência for menor que:
                       </Typography>
                   </Grid>
                   <Grid item xs={12} sm={4}>
                       <TextField
                           fullWidth
                           size="small"
                           label="Meses"
                           type="number"
                           value={rule.value_left || ''}
                           onChange={handleValueChange}
                           InputLabelProps={{ shrink: true }}
                           InputProps={{ endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>meses</Typography> }}
                       />
                   </Grid>
              </Grid>
         );
       }
      // --- Caso: Múltipla Escolha ---
      if (normalizedType === 'select' || normalizedType === 'multiselect' || normalizedType.includes('múltipla escolha')) {
           return (
              <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={5}>
                      <TextField select fullWidth size="small" label="Condição" value={rule.operator || '='} onChange={handleOperatorChange}>
                          {CHOICE_OPERATORS.map(op => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                      </TextField>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                      <TextField
                          select
                          fullWidth
                          size="small"
                          label="Valor (Opção)"
                          value={rule.value_left || ''}
                          onChange={handleValueChange}
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{ displayEmpty: true }}
                          disabled={!availableOptions.length}
                      >
                          <MenuItem value="" disabled>Selecione a opção</MenuItem>
                          {availableOptions.map((opt) => (
                              <MenuItem key={opt.id || opt.label} value={opt.label}>
                                  {opt.label || '(Opção sem texto)'}
                              </MenuItem>
                          ))}
                      </TextField>
                  </Grid>
              </Grid>
          );
      }
      // --- Caso Padrão (Texto) ---
      return (
          <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={5}>
                  <TextField select fullWidth size="small" label="Condição" value={rule.operator || '='} onChange={handleOperatorChange}>
                     {TEXT_OPERATORS.map(op => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                  </TextField>
              </Grid>
              <Grid item xs={12} sm={7}>
                  <TextField fullWidth size="small" label="Valor" value={rule.value_left || ''} onChange={handleValueChange} InputLabelProps={{ shrink: true }} />
              </Grid>
          </Grid>
      );
  };

  /**
   * Componente interno para exibir um preview de como o candidato verá
   * o input de 'Experiência'.
   */
  const ExperiencePreview = () => (
      <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={1}>
          <Typography variant="overline" display="block" gutterBottom color="text.secondary">
             <InfoOutlined fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }}/>
              Como o candidato verá (exemplo):
          </Typography>
          <Stack spacing={1} mt={1}>
              {/* Exemplo de item já adicionado */}
              <Paper variant="outlined" sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'action.hover' }}>
                  <Box>
                      <Typography fontWeight="bold">Empresa Exemplo Ltda</Typography>
                      <Typography variant="body2" color="text.secondary">01/01/2024 – 31/08/2024</Typography>
                  </Box>
                  <IconButton disabled size="small" aria-label="Remover experiência (exemplo)"><Delete fontSize="small" /></IconButton>
              </Paper>
               {/* Formulário de adição (desabilitado) */}
              <Paper sx={{ p: 2, border: '1px dashed #ccc', opacity: 0.7 }}>
                  <Grid container spacing={1}>
                      <Grid item xs={12}><TextField label="Nome da Empresa" fullWidth size="small" disabled /></Grid>
                      <Grid item xs={12} sm={6}><TextField label="Data de Início" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled helperText="dd/mm/aaaa" /></Grid>
                      <Grid item xs={12} sm={6}><TextField label="Data de Fim (opcional)" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} disabled helperText="dd/mm/aaaa" /></Grid>
                  </Grid>
                  <Button startIcon={<Add />} sx={{ mt: 1 }} disabled>Adicionar Experiência</Button>
              </Paper>
          </Stack>
      </Box>
  );

  // Renderização do Dialog
  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
          m: { xs: 1.5, sm: 4 },
        },
      }}
    >
      <DialogTitle>{form.id ? 'Editar Pergunta' : 'Nova Pergunta'}</DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack spacing={3}>
            {/* Campos da Pergunta */}
            <TextField label="Texto da Pergunta *" value={form.label || ''} onChange={(e) => setForm({ ...form, label: e.target.value })} multiline rows={2} required autoFocus />
            <TextField
              select
              label="Tipo de Resposta *"
              value={form.type || 'text'}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            >
              {QUESTION_TYPES.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </TextField>
            <FormControlLabel control={<Switch checked={!!form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />} label="Obrigatória" />

            {/* Preview para Experiência */}
            {isExperience && <ExperiencePreview />}

            {/* Seção de Opções (Condicional) */}
            {showOptions && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Opções de Resposta *</Typography>
                    <Stack spacing={2}>
                        {options.map((opt, index) => (
                            <Stack direction="row" spacing={1} key={opt.id || `option-${index}`} alignItems="center">
                                <TextField
                                   label={`Texto da Opção ${index + 1} *`}
                                   value={opt.label || ''}
                                   onChange={e => updateOption(index, 'label', e.target.value)}
                                   sx={{ flex: 1 }}
                                   required
                                />
                                <Tooltip title="Remover Opção">
                                    <IconButton color="error" onClick={() => removeOption(index)} aria-label="Remover Opção"><Delete fontSize="small" /></IconButton>
                                </Tooltip>
                            </Stack>
                        ))}
                    </Stack>
                    <Button onClick={addOption} sx={{ mt: 2 }} startIcon={<Add />}>Adicionar Opção</Button>
                </Paper>
            )}

            {/* Seção de Regras (Condicional) */}
            {canHaveRules && (
                <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
                    <Typography variant="h6">Regra de reprovação</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                        Defina a condição e o valor da resposta que reprovarão o candidato automaticamente.
                    </Typography>
                    <Stack spacing={2}>
                        {rules.map((rule, index) => (
                            <Fragment key={rule.id || `rule-${index}`}>
                                <Paper sx={{ p: { xs: 1.25, sm: 2 }, border: '1px solid #eee', minWidth: 0 }}>
                                    <Stack
                                      direction={{ xs: 'column', sm: 'row' }}
                                      alignItems={{ xs: 'stretch', sm: 'center' }}
                                      spacing={1}
                                    >
                                        <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
                                            {renderRuleInputs(
                                                form.type,
                                                rule,
                                                (idx, data) => handleUpdateRule(idx, data),
                                                index,
                                                options
                                            )}
                                        </Box>
                                        <Tooltip title="Remover Regra">
                                            {!(isExperience && rules.length === 1 && index > 0) && ( 
                                                <IconButton
                                                  color="error"
                                                  onClick={() => handleRemoveRule(index)}
                                                  size="small"
                                                  aria-label="Remover Regra"
                                                  sx={{ alignSelf: { xs: 'flex-end', sm: 'center' }, flexShrink: 0 }}
                                                >
                                                  <Delete fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Tooltip>
                                    </Stack>
                                </Paper>
                                {/* Separador "OU" só aparece se NÃO for experience */}
                                {!isExperience && index < rules.length - 1 && (
                                    <Typography align="center" sx={{ fontWeight: 'bold', color: 'text.secondary', my: -1 }}>OU</Typography>
                                )}
                            </Fragment>
                        ))}
                    </Stack>
                    
                    {/* Botão "Adicionar regra" só aparece se NÃO for experience */}
                    {!isExperience && (
                        <Button
                            onClick={handleAddRule}
                            sx={{ mt: 2 }}
                            startIcon={<Add />}
                        >
                            Adicionar regra de reprovação
                        </Button>
                    )}
                     {/* Mensagem informativa para experience */}
                     {isExperience && rules.length === 0 && (
                         <Button onClick={handleAddRule} sx={{ mt: 2 }} startIcon={<Add />}>
                            Adicionar regra de reprovação (mínimo de meses)
                         </Button>
                     )}
                     {isExperience && rules.length > 0 && (
                         <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                             Apenas uma regra (média de meses) é permitida para este tipo de pergunta.
                         </Typography>
                     )}
                </Paper>
            )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, flexWrap: 'wrap' }}>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar Pergunta'}</Button>
      </DialogActions>
    </Dialog>
  );
}


/**
 * Componente de página principal (Admin) para editar um formulário (cv_form).
 * Gerencia o título do formulário e a lista/ordenação de perguntas (cv_question).
 * Delega a criação/edição de perguntas para o QuestionDialog.
 * @param {{formId: string, onBack: function}} props
 * @param {string} props.formId - ID do formulário a ser editado.
 * @param {function} props.onBack - Callback para voltar à lista de formulários.
 */
export default function FormEditor({ formId, onBack, embedded = false, initialQuestionId = null }) {
  const [form, setForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteQuestionId, setDeleteQuestionId] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [updatingTitle, setUpdatingTitle] = useState(false);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const autoOpenedQuestionRef = useRef(null);

  /**
   * Ordena a lista de perguntas, priorizando a 'position'
   * e usando 'created_at' como desempate.
   */
  const sortQuestions = useCallback((list) => {
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) => {
      const posA = Number.isFinite(Number(a?.position)) ? Number(a.position) : Infinity;
      const posB = Number.isFinite(Number(b?.position)) ? Number(b.position) : Infinity;
      if (posA !== posB) return posA - posB;
      const createdA = a?.created_at ?? '';
      const createdB = b?.created_at ?? '';
      return String(createdA).localeCompare(String(createdB));
    });
  }, []);

  /**
   * Carrega os dados do formulário e sua lista de perguntas da API.
   */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [formData, questionsData] = await Promise.all([
        getCvFormDetails(formId),
        getCvFormQuestions(formId),
      ]);
      setForm(formData);
      setTitleDraft(formData?.title ?? '');
      setQuestions(sortQuestions(questionsData));
    } catch (err) {
      toast.error('Erro ao carregar dados da etapa.');
      console.error(err);
      onBack();
    } finally {
      setLoading(false);
    }
  }, [formId, onBack, sortQuestions]);

  useEffect(() => {
    if (formId) load();
  }, [formId, load]);

  useEffect(() => {
    autoOpenedQuestionRef.current = null;
  }, [formId]);

  useEffect(() => {
    if (!initialQuestionId || loading || !Array.isArray(questions) || questions.length === 0) return;

    const targetId = String(initialQuestionId);
    if (autoOpenedQuestionRef.current === targetId) return;

    const targetQuestion = questions.find((q) => String(q?.id) === targetId);
    if (!targetQuestion) return;

    setEditingQuestion(targetQuestion);
    setDialogOpen(true);
    autoOpenedQuestionRef.current = targetId;
  }, [initialQuestionId, loading, questions]);

  const openNewQuestion = () => {
    setEditingQuestion(null);
    setDialogOpen(true);
  };

  const openEditQuestion = (q) => {
    setEditingQuestion(q);
    setDialogOpen(true);
  };

  /**
   * Callback passada para o QuestionDialog.
   * Chama a API para criar ou atualizar a pergunta.
   * @param {object} questionData - Dados da pergunta (sem opções/regras).
   * @returns {Promise<object>} - A pergunta salva (criada ou atualizada).
   */
  const handleSaveQuestion = async (questionData) => {
    if (questionData.id) {
      // Atualiza
      const updatedQuestion = await updateCvQuestion(questionData.id, questionData);
      return updatedQuestion;
    } else {
      // Cria
      // ✅ CORREÇÃO: Removemos a desestruturação { data }
      const newQuestion = await createCvQuestion(formId, questionData);
      return newQuestion; // Retorna o objeto da pergunta diretamente
    }
  };

  /**
   * Fecha o QuestionDialog e, opcionalmente, recarrega a lista de perguntas.
   * @param {boolean} [shouldReload=false] - Se deve recarregar os dados.
   */
  const handleCloseDialog = (shouldReload = false) => {
    setDialogOpen(false);
    setEditingQuestion(null);
    if (shouldReload) {
        load();
    }
  }

  /**
   * Manipulador para deletar uma pergunta (via ConfirmDialog).
   */
  const handleDeleteQuestion = async () => {
    if (!deleteQuestionId) return;
    try {
      await deleteCvQuestion(deleteQuestionId);
      toast.success('Pergunta removida.');
      setDeleteQuestionId(null);
      await load();
    } catch (err) {
      toast.error('Erro ao remover a pergunta.');
      console.error(err);
      setDeleteQuestionId(null);
    }
  };

  /**
   * Atualiza o título do formulário (onBlur do TextField).
   */
  const handleUpdateTitle = async () => {
    const trimmed = titleDraft.trim();
    if (!trimmed) {
      toast.warning('Informe um título para a etapa.');
      return;
    }
    if (trimmed === (form?.title || '')) {
      return; // Sem mudanças
    }
    setUpdatingTitle(true);
    try {
      await updateCvForm(formId, { title: trimmed });
      toast.success('Título da etapa atualizado.');
      setForm((prev) => (prev ? { ...prev, title: trimmed } : prev));
    } catch (err) {
      console.error(err);
      toast.error('Erro ao atualizar o título da etapa.');
      setTitleDraft(form?.title || ''); // Reverte para o original
    } finally {
      setUpdatingTitle(false);
    }
  };

  // --- Lógica de Reordenação (Drag and Drop) ---

  const openReorderDialog = () => {
    setReorderList(sortQuestions(questions));
    setReorderDialogOpen(true);
  };

  /**
   * Função utilitária para mover um item em uma lista (usada pelo dnd).
   */
  const reorderItems = useCallback((list, startIndex, endIndex) => {
    const updated = Array.from(list);
    const [moved] = updated.splice(startIndex, 1);
    updated.splice(endIndex, 0, moved);
    return updated;
  }, []);

  /**
   * Callback do DragDropContext ao soltar um item.
   */
  const handleDragEnd = useCallback(({ destination, source }) => {
    if (!destination || destination.index === source.index) return;
    setReorderList((prev) => reorderItems(prev, source.index, destination.index));
  }, [reorderItems]);

  /**
   * Salva a nova ordem das perguntas na API.
   */
  const handleSaveOrder = async () => {
    if (!Array.isArray(reorderList) || reorderList.length === 0) {
      setReorderDialogOpen(false);
      return;
    }
    setSavingOrder(true);
    try {
      await reorderCvQuestions(formId, reorderList.map((q) => q.id));
      toast.success('Ordem das perguntas atualizada.');
      setReorderDialogOpen(false);
      setQuestions(reorderList); // Atualiza o estado local para refletir a nova ordem
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar a nova ordem das perguntas.');
    } finally {
      setSavingOrder(false);
    }
  };

  // --- Renderização Principal ---
  if (loading) return <Typography sx={{ p: 2 }}>Carregando editor...</Typography>;
  if (!form) return <Typography sx={{ p: 2, color: 'error.main' }}>Erro: Não foi possível carregar os dados do formulário.</Typography>;

  return (
    <Box p={embedded ? 1.5 : 2}>
      {!embedded && (
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
          Voltar para Etapas
        </Button>
      )}
      
      {/* Editor do Título */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Detalhes da etapa</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            label="Título da etapa *"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 200 }}
            onBlur={handleUpdateTitle}
            disabled={updatingTitle}
          />
        </Stack>
      </Paper>

      {/* Cabeçalho da Lista de Perguntas */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6">Perguntas</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" startIcon={<Sort />} onClick={openReorderDialog} disabled={questions.length < 2}>
            Ordenar perguntas
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openNewQuestion}>Adicionar Pergunta</Button>
        </Stack>
      </Box>

      {/* Lista de Perguntas */}
      <Box display="flex" flexDirection="column" gap={2}>
        {questions.map((q, index) => {
            const showOptions = q.type === 'select' || q.type === 'multiselect';
            const canHaveRules = q.type !== 'text';
            return (
                <Paper key={q.id} variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography fontWeight="bold" noWrap>
                           {`${index + 1}. ${q.label || '(Pergunta sem texto)'}`}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" gap={0.5}>
                          <Chip label={QUESTION_TYPES.find(t => t.value === q.type)?.label || q.type} size="small" variant="outlined" />
                          {q.required && <Chip label="Obrigatória" size="small" color="primary" variant="outlined"/>}
                          {showOptions && q.options?.length > 0 && <Chip label={`${q.options.length} Opções`} size="small" variant="outlined" />}
                          {canHaveRules && q.rules?.length > 0 && (
                            <Chip label="Regra de reprovação ativa" color="error" size="small" variant="outlined" />
                          )}
                        </Stack>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                        <Tooltip title="Editar Pergunta"><IconButton size="small" onClick={() => openEditQuestion(q)} aria-label="Editar Pergunta"><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Excluir Pergunta"><IconButton size="small" color="error" onClick={() => setDeleteQuestionId(q.id)} aria-label="Excluir Pergunta"><Delete fontSize="small" /></IconButton></Tooltip>
                    </Box>
                    </Box>
                </Paper>
            );
        })}
        {questions.length === 0 && <Typography color="text.secondary" textAlign="center" sx={{ p: 3 }}>Nenhuma pergunta adicionada.</Typography>}
      </Box>

      {/* Diálogo para Adicionar/Editar Pergunta */}
      {dialogOpen && (
        <QuestionDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSave={handleSaveQuestion}
          question={editingQuestion}
        />
      )}
      
      {/* Diálogo Confirmar Exclusão */}
       <ConfirmDialog
        open={!!deleteQuestionId}
        title="Excluir Pergunta"
        message="Deseja realmente excluir esta pergunta, suas opções e regras associadas? Esta ação não pode ser desfeita."
        onConfirm={handleDeleteQuestion}
        onClose={() => setDeleteQuestionId(null)}
      />

      {/* Diálogo Reordenar */}
      <Dialog open={reorderDialogOpen} onClose={() => !savingOrder && setReorderDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ordenar perguntas</DialogTitle>
        <DialogContent dividers>
          {reorderList.length === 0 ? (
            <Typography color="text.secondary">Nenhuma pergunta para ordenar.</Typography>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="questions-order">
                {(provided) => (
                  <List ref={provided.innerRef} {...provided.droppableProps} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {reorderList.map((question, index) => (
                      <Draggable key={question.id} draggableId={String(question.id)} index={index} isDragDisabled={savingOrder}>
                        {(draggableProvided, snapshot) => (
                          <ListItem
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            sx={{
                              borderRadius: 1, border: '1px solid', borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                              bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper', boxShadow: snapshot.isDragging ? 3 : 0, px: 2, py: 1.5,
                            }}
                          >
                            <ListItemIcon {...draggableProvided.dragHandleProps} sx={{ minWidth: 32, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', cursor: snapshot.isDragging ? 'grabbing' : 'grab' }}>
                              <DragIndicator />
                            </ListItemIcon>
                            <ListItemText
                              primary={question.label || 'Pergunta sem título'}
                              secondary={QUESTION_TYPES.find((t) => t.value === question.type)?.label || question.type}
                            />
                          </ListItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReorderDialogOpen(false)} disabled={savingOrder}>Cancelar</Button>
          <Button onClick={handleSaveOrder} variant="contained" disabled={savingOrder || reorderList.length === 0}>
            {savingOrder ? 'Salvando...' : 'Salvar ordem'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
